import React, { useState, useEffect } from 'react';

import classNames from 'classnames';

import { Button } from '../../Button';
import { Icon } from '../../Icon';
import { useIsDesktop } from '../../hooks';

import { PreviewButtons } from './Buttons';
import { useStyles } from './Preview.styles';

/**
 * Интерфейс пропсов компонента PreviewImage.
 * @interface PreviewImageProps
 * @property {string} url - URL изображения для предварительного просмотра.
 * @property {'auto' | 'square'} shape - Форма отображения изображения ('auto' или 'square').
 * @property {boolean} clickable - Определяет, является ли изображение кликабельным.
 * @property {() => Promise<Blob | { file: Promise<Blob>; filename?: string }>} [onClickDownload] - Функция для загрузки изображения.
 * @property {boolean} [withCollapsableFooter] - Определяет, нужно ли отображать сворачиваемый нижний колонтитул.
 * @property {() => void} [onLoadSuccess] - Коллбэк, вызываемый после успешной загрузки изображения.
 * @property {() => void} [onExpand] - Коллбэк, вызываемый для раскрытия превью изображения.
 * @property {boolean} [centerImage] - Определяет, нужно ли центрировать изображение.
 */
interface PreviewImageProps {
  url: string;
  shape: 'auto' | 'square';
  clickable: boolean;
  onClickDownload?: () => Promise<
    Blob | { file: Promise<Blob>; filename?: string }
  >;
  withCollapsableFooter?: boolean;
  centerImage?: boolean;
  onLoadSuccess?: () => void;
  onLoadFull?: () => Promise<Blob>;
  onExpand?: () => void;
}

/**
 * Компонент для предварительного просмотра изображения.
 * @param {PreviewImageProps} props - Пропсы компонента.
 * @returns {React.ReactElement} Элемент React.
 */
export const PreviewImage = ({
  url,
  shape,
  clickable,
  onClickDownload,
  withCollapsableFooter,
  centerImage,
  onLoadSuccess,
  onExpand,
  onLoadFull,
}: PreviewImageProps) => {
  const { classes } = useStyles();

  const isDesktop = useIsDesktop();
  const [objectUrl, setObjectUrl] = useState<string>('');

  const handleImageLoad = () => {
    if (onLoadSuccess) {
      onLoadSuccess();
    }
  };

  useEffect(() => {
    return () => {
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [objectUrl]);

  const imageClasses = classNames(
    classes.image,
    shape === 'square' && classes.image_square,
  );

  return (
    <>
      <div
        className={classNames(
          classes.root,
          classes.collapseWrapper,
          centerImage && classes.centerImage,
          'aqa_file_preview_image',
        )}
      >
        {shape === 'square' && <div className={classes.square} />}
        {clickable ? (
          <a
            href={url}
            onClick={(e) => {
              if (onLoadFull) {
                e.preventDefault();
                e.stopPropagation();

                onLoadFull()
                  .then((blob) => {
                    const nextObjUrl = URL.createObjectURL(blob);
                    if (objectUrl) {
                      URL.revokeObjectURL(objectUrl);
                    }

                    setObjectUrl(nextObjUrl);

                    return nextObjUrl;
                  })
                  .catch(() => url)
                  .then((objUrl) => {
                    window.open(objUrl, '_blank');
                  });
              }
            }}
            target="_blank"
            rel="noreferrer"
          >
            <img
              className={imageClasses}
              src={url}
              alt=""
              onLoad={handleImageLoad}
            />
          </a>
        ) : (
          <img
            className={imageClasses}
            src={url}
            alt=""
            onLoad={handleImageLoad}
          />
        )}
        {onClickDownload && (
          <PreviewButtons
            withCollapsableFooter={withCollapsableFooter}
            onClickDownload={onClickDownload}
          />
        )}
      </div>
      {onExpand && isDesktop && (
        <div className={classes.expandButton}>
          <Button
            icon={<Icon name="fullscreen_outline" size="20" />}
            size="small"
            onClick={onExpand}
          />
        </div>
      )}
    </>
  );
};
